<template>
  <div class="basic-info-step">
    <div class="form-section">
      <!-- Forwarder Name -->
      <div class="field">
        <label class="label">
          {{ $t('forwarders.forwarderName') }}
          <span class="required-mark">*</span>
          <span 
            class="info-icon clickable" 
            @click="showNameTooltip = !showNameTooltip"
          >(!)</span>
          <div v-if="showNameTooltip" class="tooltip-content">
            <p><strong>{{ $t('forwarders.nameRules') }}:</strong></p>
            <ul>
              <li>{{ $t('forwarders.nameRuleNotEmpty') }}</li>
              <li>{{ $t('forwarders.nameRuleNotDuplicate') }}</li>
              <li>{{ $t('forwarders.nameRuleNoSpace') }}</li>
              <li>{{ $t('forwarders.nameRuleLength') }}</li>
            </ul>
          </div>
        </label>
        <div class="control">
          <input
            ref="nameInput"
            v-model="localValue.name"
            class="input"
            :class="{ 'is-danger': nameError && nameTouched }"
            type="text"
            :placeholder="$t('forwarders.enterForwarderName')"
            maxlength="30"
            :disabled="isEditMode"
            :readonly="isEditMode"
            @input="validateName"
            @blur="handleNameBlur"
          >
        </div>
        <p v-if="nameError" class="help is-danger">{{ nameError }}</p>
      </div>

      <!-- Description -->
      <div class="field">
        <label class="label">{{ $t('description') }}</label>
        <div class="control">
          <textarea
            v-model="localValue.description"
            class="textarea"
            :placeholder="$t('forwarders.enterDescription')"
            maxlength="4096"
            rows="3"
          ></textarea>
        </div>
        <div class="char-count">{{ localValue.description.length }}/4096</div>
      </div>

      <!-- Token -->
      <div class="field">
        <label class="label">
          {{ $t('token') }}
          <span class="required-mark">*</span>
          <span 
            class="info-icon clickable" 
            @click="showTokenTooltip = !showTokenTooltip"
          >(?)</span>
          <div v-if="showTokenTooltip" class="tooltip-content">
            <p>{{ $t('forwarders.tokenHelp') }}</p>
          </div>
        </label>
        <div class="field has-addons">
          <div class="control is-expanded">
            <input
              v-model="localValue.token"
              class="input"
              type="text"
              readonly
              disabled
            >
          </div>
          <div class="control">
            <button
              type="button"
              class="button"
              :title="$t('forwarders.regenerateToken')"
              :disabled="isEditMode"
              @click="regenerateToken"
            >
              <octicon :icon="icons.sync" />
            </button>
          </div>
          <div class="control">
            <button
              type="button"
              class="button"
              :title="$t('forwarders.copyToken')"
              @click="copyToken"
            >
              <octicon :icon="icons.clippy" />
            </button>
          </div>
        </div>
      </div>

      <!-- Forwarder Type Selection -->
      <div class="field">
        <label class="label">{{ $t('forwarders.forwarderType') }}</label>
        <div class="module-selection">
          <div class="module-item">
            <input
              id="module-system-log"
              v-model="selectedModules.system_log"
              type="checkbox"
              class="checkbox"
              :disabled="!isBasicInfoValid"
              @change="updateModules"
            >
            <label for="module-system-log" class="module-label" :class="{ 'disabled': !isBasicInfoValid }">
              {{ $t('forwarders.systemLog') }}
            </label>
          </div>

          <div class="module-item">
            <input
              id="module-resource-monitor"
              v-model="selectedModules.resource_monitor"
              type="checkbox"
              class="checkbox"
              :disabled="!isBasicInfoValid"
              @change="updateModules"
            >
            <label for="module-resource-monitor" class="module-label" :class="{ 'disabled': !isBasicInfoValid }">
              {{ $t('forwarders.resourceMonitor') }}
            </label>
          </div>

          <div class="module-item">
            <input
              id="module-file-log"
              v-model="selectedModules.file_log"
              type="checkbox"
              class="checkbox"
              :disabled="!isBasicInfoValid"
              @change="updateModules"
            >
            <label for="module-file-log" class="module-label" :class="{ 'disabled': !isBasicInfoValid }">
              {{ $t('forwarders.fileLog') }}
            </label>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as icons from 'octicons-vue'
import clipboard from '@/clipboard.js'

export default {
  name: 'BasicInfoStep',
  props: {
    value: {
      type: Object,
      required: true
    },
    validationErrors: {
      type: Object,
      default: () => ({})
    },
    initialSelectedModules: {
      type: Object,
      default: () => ({
        system_log: false,
        resource_monitor: false,
        file_log: false
      })
    },
    isEditMode: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      localValue: { ...this.value },
      selectedModules: { ...this.initialSelectedModules },
      nameError: '',
      nameTouched: false,
      showNameTooltip: false,
      showTokenTooltip: false
    }
  },
  computed: {
    icons() {
      return icons
    },
    isBasicInfoValid() {
      // Check if name is valid and token exists
      const name = this.localValue.name.trim()

      if (!name || name.length < 3 || name.length > 30) {
        return false
      }

      // Check for invalid characters
      const validNamePattern = /^[a-zA-Z0-9._-]+$/
      if (!validNamePattern.test(name)) {
        return false
      }

      // Check if token exists
      if (!this.localValue.token || this.localValue.token.trim() === '') {
        return false
      }

      return true
    }
  },
  watch: {
    initialSelectedModules: {
      handler(newVal) {
        this.selectedModules = { ...newVal }
      },
      deep: true,
      immediate: true
    },
    value: {
      handler(newVal) {
        // Only update if different to avoid infinite loop
        if (JSON.stringify(this.localValue) !== JSON.stringify(newVal)) {
          this.localValue = { ...newVal }
        }
      },
      deep: true
    },
    'localValue.name': {
      handler(newVal) {
        this.$emit('input', this.localValue)
      }
    },
    'localValue.description': {
      handler(newVal) {
        this.$emit('input', this.localValue)
      }
    },
    'localValue.token': {
      handler(newVal) {
        this.$emit('input', this.localValue)
      }
    }
  },
  mounted() {
    // Auto-generate token if not exists
    if (!this.localValue.token || this.localValue.token.trim() === '') {
      this.regenerateToken()
    }

    // Add click outside listener for tooltips
    document.addEventListener('click', this.handleClickOutside)
  },
  beforeDestroy() {
    // Remove click outside listener
    document.removeEventListener('click', this.handleClickOutside)
  },
  methods: {
    handleClickOutside(event) {
      // Check if click is outside tooltip areas
      const tooltipTriggers = this.$el.querySelectorAll('.info-icon.clickable')
      const tooltipContents = this.$el.querySelectorAll('.tooltip-content')
      
      let clickedInsideTooltip = false
      
      // Check if clicked on trigger or content
      tooltipTriggers.forEach(trigger => {
        if (trigger.contains(event.target)) {
          clickedInsideTooltip = true
        }
      })
      
      tooltipContents.forEach(content => {
        if (content.contains(event.target)) {
          clickedInsideTooltip = true
        }
      })
      
      // If clicked outside, close all tooltips
      if (!clickedInsideTooltip) {
        this.showNameTooltip = false
        this.showTokenTooltip = false
      }
    },
    validateName() {
      const name = this.localValue.name.trim()
      
      if (!name) {
        this.nameError = this.$t('forwarders.pleaseEnterForwarderName')
        this.$emit('name-validation-error', true)
        return false
      }
      
      if (name.length < 3) {
        this.nameError = this.$t('forwarders.nameTooShort')
        this.$emit('name-validation-error', true)
        return false
      }
      
      if (name.length > 30) {
        this.nameError = this.$t('forwarders.nameTooLong')
        this.$emit('name-validation-error', true)
        return false
      }
      
      // Check for invalid characters (only allow alphanumeric, dash, underscore, dot)
      const validNamePattern = /^[a-zA-Z0-9._-]+$/
      if (!validNamePattern.test(name)) {
        this.nameError = this.$t('forwarders.nameInvalidCharacters')
        this.$emit('name-validation-error', true)
        return false
      }
      
      this.nameError = ''
      this.$emit('name-validation-error', false)
      return true
    },
    async handleNameBlur() {
      this.nameTouched = true
      const isValid = this.validateName()
      if (!isValid && this.$refs.nameInput) {
        this.$nextTick(() => {
          this.$refs.nameInput.focus()
        })
        return
      }
      
      // Check if name already exists (only if basic validation passed)
      if (isValid) {
        try {
          const params = { name: this.localValue.name.trim() }
          // In edit mode, exclude current forwarder ID
          if (this.isEditMode && this.localValue.id) {
            params.exclude_id = this.localValue.id
          }
          const response = await this.$http.get('/api/v1/forwarders/check-name', { params })
          if (response && response.data && response.data.data && response.data.data.exists) {
            this.nameError = this.$t('forwarders.nameExists')
            this.$emit('name-validation-error', true)
            if (this.$refs.nameInput) {
              this.$nextTick(() => {
                this.$refs.nameInput.focus()
              })
            }
          }
        } catch (error) {
          console.error('Error checking name exists:', error)
        }
      }
    },
    regenerateToken() {
      const token = 'xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx'.replace(/x/g, () => {
        return Math.floor(Math.random() * 16).toString(16)
      })
      this.localValue.token = token
    },
    copyToken() {
      clipboard(this.localValue.token || '')
      this.$store.dispatch('NOTIFY', {
        type: 'success',
        text: this.$t('forwarders.tokenCopied')
      })
    },
    updateModules() {
      this.$emit('update:selectedModules', { ...this.selectedModules })
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/styles/dark-modal.scss';

.basic-info-step {
  .form-section {
    max-width: 600px;
  }

  .field {
    margin-bottom: 1rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .label {
    color: $modal-text;
    font-weight: 500;
    margin-bottom: 0.35rem;
    display: block;
    position: relative;

    .required-mark {
      color: $modal-required;
      margin-left: 2px;
    }

    .info-icon {
      display: inline-block;
      width: 16px;
      height: 16px;
      line-height: 16px;
      text-align: center;
      border-radius: 50%;
      background-color: rgba(255, 255, 255, 0.1);
      color: #fff;
      font-size: 0.75rem;
      margin-left: 4px;
      font-style: normal;
      transition: all 0.2s;

      &.clickable {
        cursor: pointer;

        &:hover {
          background-color: rgba(255, 255, 255, 0.2);
          transform: scale(1.1);
        }
      }
    }

    .tooltip-content {
      position: absolute;
      top: 100%;
      left: 0;
      margin-top: 0.5rem;
      padding: 1rem;
      background-color: #2a2d36;
      border: 1px solid #404553;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
      z-index: 1000;
      min-width: 300px;
      max-width: 400px;

      p {
        color: #fff;
        margin: 0 0 0.5rem 0;
        font-size: 0.9rem;

        strong {
          color: $modal-primary;
        }
      }

      ul {
        list-style: none;
        padding: 0;
        margin: 0;

        li {
          color: rgba(255, 255, 255, 0.85);
          font-size: 0.85rem;
          line-height: 1.6;
          padding-left: 1rem;
          position: relative;
          margin-bottom: 0.25rem;

          &:before {
            content: "•";
            position: absolute;
            left: 0;
            color: $modal-primary;
          }

          &:last-child {
            margin-bottom: 0;
          }
        }
      }
    }
  }

  .control {
    .input,
    .textarea {
      background-color: $modal-input-bg;
      border: 1px solid $modal-border;
      color: $modal-text;
      border-radius: 4px;
      padding: 0.75rem;
      width: 100%;
      font-size: 0.95rem;

      &:focus {
        border-color: $modal-primary;
        outline: none;
      }

      &.is-danger {
        border-color: $modal-required;
        box-shadow: 0 0 0 2px rgba(255, 82, 82, 0.2);
      }

      &:disabled,
      &[readonly] {
        background-color: $modal-bg;
        cursor: not-allowed;
        opacity: 0.7;
      }

      &::placeholder {
        color: $modal-placeholder;
      }
    }

    .textarea {
      resize: vertical;
      min-height: 80px;
    }
  }

  .field.has-addons {
    display: flex;
    gap: 0;

    .control {
      &.is-expanded {
        flex: 1;
      }

      &:not(:first-child) .button {
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
      }

      &:not(:last-child) .input {
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
      }
    }

    .button {
      background-color: #40424a;
      border: 1px solid #404553;
      color: #fff;
      padding: 0.75rem 1rem;
      cursor: pointer;
      transition: all 0.2s;

      &:hover {
        background-color: #4a4d57;
      }

      &:active {
        transform: scale(0.95);
      }
    }
  }

  .char-count {
    text-align: right;
    color: rgba(255, 255, 255, 0.5);
    font-size: 0.85rem;
    margin-top: 0.25rem;
  }

  .help {
    color: $modal-text-muted;
    font-size: 0.85rem;
    margin-top: 0.25rem;

    &.is-danger {
      color: $modal-required;
    }
  }

  .module-selection {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 1rem;
    background-color: rgba(255, 255, 255, 0.05);
    border-radius: 4px;

    .module-item {
      display: flex;
      align-items: center;
      gap: 0.75rem;

      .checkbox {
        width: 18px;
        height: 18px;
        cursor: pointer;
        accent-color: $modal-primary;

        &:disabled {
          cursor: not-allowed;
          opacity: 0.5;
        }
      }

      .module-label {
        color: $modal-text;
        font-size: 0.95rem;
        cursor: pointer;
        user-select: none;
        margin: 0;

        &.disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      }
    }
  }
}
</style>
